import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Group, Rect, Text } from 'react-konva';
import { adaptImageSizes } from '../canvas/utils.js';
import { dataURLtoFile } from '../builder/ImageUploader.js';
import { CardPlayerImage } from './PickFavoriteCanvas.js';
import { isNotEmpty } from '../../lib/ep.js';
import { localizeFields } from '../builder/DynamicFields.js';

const punch_item_size = 60;	// px
const player_padding = 10;

const standard_board_size = { w:600, h:600 };	// px

const FaceOffFinalCanvas = forwardRef((props, ref) => {
	const stage = props.stage;

	const [ full_loaded, setFullLoaded ] = useState(false);

	const bg_image = props.indexed_images?.final_bg_img ?? false;

	const stage_ref = useRef();

	const players = localizeFields(stage?.faceoff_players ?? [],props.locale);
	const text_border_width = parseInt(stage?.faceoff_text_border_width ?? '0');
	const display_losers = props?.display_losers ?? (stage?.faceoff_display_losers ?? false);
	const display_player_text = stage?.faceoff_display_player_text ?? true;
	const text_color_overwrite = stage?.faceoff_card_text_color_overwrite ?? true;
	const text_color = stage?.text_color ?? 'rgba(0, 0, 0, 1)';

	const player_text_padding = 20;
	const player_block_text_h = 100;

	const refs = useRef([]);
	const size = useRef({ w:standard_board_size.w, h:standard_board_size.h });
	const item_sizes = useRef({});
	const canvas_scale = useRef(size.current.w / standard_board_size.w);
	const player_text_h = useRef(player_block_text_h * canvas_scale.current);
	const padding = useRef(player_padding * canvas_scale.current);
	const contents_h = useRef(0);

	const calculatePlayerSize = (use_default_size = false) => {
		// we force the size as if there are 2 items per row
		const forced_items_per_row = 1.5;

		const canvas_w = (use_default_size === false) ? size.current.w : standard_board_size.w;

		return (canvas_w - padding.current * (forced_items_per_row + 1)) / forced_items_per_row;
	};

	const player_scale = useRef(calculatePlayerSize() / calculatePlayerSize(true));

	const losers_per_row = parseInt(stage?.faceoff_loser_cols ?? 3);
	const losers_inline_margin = 50;

	const generateItemSizes = () => {
		const player_size = calculatePlayerSize();
		const shadow_w = player_size * 0.9;

		item_sizes.current = {
			player_size,
			player_text_w:1.1 * player_size - player_text_padding,
			player_x_ini:0,
			player_y_ini:player_size / 2 + padding.current,
			shadow_w,
			shadow_h:player_size / 8,
			shadow_x_ini:(player_size - shadow_w) / 2,
			shadow_y_ini:player_size / 1.8,
			loser_size:(size.current.w - padding.current * (losers_per_row - 1) - 2 * losers_inline_margin) / losers_per_row,
		};
	};

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	const gameInit = () => {
		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((size_received) => {
				size.current.w = size_received;
				size.current.h = size_received;
				canvas_scale.current = size.current.w / standard_board_size.w;
				player_text_h.current = player_block_text_h * canvas_scale.current;
				padding.current = player_padding * canvas_scale.current;
				player_scale.current = calculatePlayerSize() / calculatePlayerSize(true);

				generateItemSizes();

				if(display_losers) {
					const num_row_losers = Math.ceil((players.length - 1) / losers_per_row);

					size.current.h += item_sizes.current.loser_size * num_row_losers + padding.current * (num_row_losers - 1) + 2 * losers_inline_margin;
				}

				const all_text_heights = player_text_h.current;

				contents_h.current = item_sizes.current.player_size + 2 * padding.current + all_text_heights;

				setTimeout(() => {
					applyAllCaches();

					mountBoard();
				}, 100);

				setFullLoaded(true);
			}, '.final_image');
		} else {
			generateItemSizes();
			setFullLoaded(true);
		}
	};

	useEffect(() => {
		setFullLoaded(false);
		if(props.loaded_status != 'LOADED') return;

		gameInit();
	}, [
		props.loaded_status,
		props.indexed_images,
		display_losers,
	]);

	const applyAllCaches = (apply_shadows = true) => {
		if(refs.current['bg'] != undefined) refs.current['bg'].cache(default_cache_props);

		if(refs.current['winner'] == undefined) return;

		if(apply_shadows && refs.current['shadow_0'] != undefined) {
			refs.current['shadow_0'].cache({ ...default_cache_props, offset:30 });
		}

		refs.current['winner'].cache(default_cache_props);
		refs.current['layer'].draw();
	};

	const mountBoard = () => {
		refs.current['winner'].to({
			opacity:1,
			duration:0.5,
			easing:Konva.Easings.StrongEaseInOut,
		});
		refs.current['layer'].draw();
	};

	useImperativeHandle(ref, () => ({
		to_image(format = 'file') {
			if(stage_ref.current != undefined) {
				let dataURL = stage_ref.current.toDataURL({
					mimeType:'image/jpeg',
					quality:0.8,
					pixelRatio:3,
				});

				if(format == 'base64') return dataURL;
				const file = dataURLtoFile(dataURL, 'filename.jpg');
				return file;
			}
		}
	}), []);

	const images_adapter = (image) => (adaptImageSizes(image, punch_item_size, 300, 500));

	const getColor = () => {
		const player_obj = players.find((p) => (p.image == indexed_images['winner']?.name));
		if(player_obj != undefined && isNotEmpty(player_obj, 'color')) return player_obj.color;
		
		const color = indexed_images['winner']?.avg_color;
		if(color != undefined) return `rgba(${color.r}, ${color.g}, ${color.b}, 1)`;

		return 'rgba(30, 34, 170, 1)';
	};

	const indexed_images = useMemo(() => {
		const adapted = {};
		for(var img in props.indexed_images) {
			adapted[img] = images_adapter(props.indexed_images[img]);
		}
		return adapted;
	}, [ props.indexed_images ]);

	if(!full_loaded) return null;

	const winner = players.find((p) => (p.id == (props.winner?.id ?? players[0].id)));
	const losers = players.filter((p) => (p.id != (props.winner?.id ?? players[0].id)));

	const text_default_props = {
		width:item_sizes.current.player_text_w,
		height:1.5 * player_text_h.current,
		align:'center',
		fontStyle:'bold',
		verticalAlign:'middle',
		fontSize:60 * player_scale.current,
		lineHeight:1.1,
		preventDefault:false,
	};

	const bg_image_scale = (bg_image !== false) ? (size.current.w / Math.min((bg_image?.image_w ?? standard_board_size.w), (bg_image?.image_h ?? standard_board_size.h))) : 1;

	return (
		<Stage
			ref={stage_ref}
			className='canvas_cont'
			width={size.current.w}
			height={size.current.h}
		>
			<Layer
				ref={(node) => { refs.current['layer'] = node; }}
			>
				<Rect
					fill='rgba(255, 255, 255, 1)'
					width={size.current.w}
					height={size.current.h}
				/>

				<Rect
					ref={(node) => { refs.current['bg'] = node; }}
					width={size.current.w}
					height={size.current.w}
					fill='black'
					fillPatternImage={bg_image?.fillPatternImage}
					fillPatternRepeat="repeat"
					fillPatternScaleX={bg_image_scale}
					fillPatternScaleY={bg_image_scale}
					fillPriority={(bg_image !== false) ? 'pattern' : 'fill'}
					opacity={(bg_image !== false) ? 1 : 0.05}
					preventDefault={false}
				/>

				{/* WINNER IMAGE - ini */}
				<Group
					ref={(node) => { refs.current['winner'] = node; }}
					x={size.current.w / 2}
					y={size.current.w / 2 - 13}
					offsetX={item_sizes.current.player_size / 2}
					offsetY={(item_sizes.current.player_size + player_text_h.current) / 2}
					preventDefault={false}
					opacity={0}
				>
					<Rect
						fill='white'
						opacity={0}
						width={item_sizes.current.player_size}
						height={item_sizes.current.player_size + player_text_h.current + 2 * padding.current}
						preventDefault={false}
					/>

					<Group
						x={size.current.w / 2 + (item_sizes.current.player_size - text_default_props.width) / 2}
						y={item_sizes.current.player_size + 15 * canvas_scale.current}
						offsetX={size.current.w / 2}
						preventDefault={false}
						opacity={(display_player_text) ? 1 : 0}
					>
						{(text_border_width > 0) && (
							<Text
								text={(isNotEmpty(winner, 'name')) ? winner.name : `Option ${1}`}
								fill='white'
								strokeWidth={text_border_width * 2}
								lineCap='round'
								lineJoin='round'
								stroke='white'
								{...text_default_props}
							/>
						)}

						<Text
							text={(isNotEmpty(winner, 'name')) ? winner.name : `Option ${1}`}
							fill={(text_color_overwrite) ? text_color : getColor()}
							{...text_default_props}
						/>
					</Group>

					<CardPlayerImage
						player={winner}
						stage={stage}
						refs={refs.current}
						image_data={indexed_images.winner}
						item_sizes={item_sizes.current}
						padding={padding.current}
						canvas_scale={canvas_scale.current}
						preventDefault={false}
						color={getColor()}
					/>
				</Group>
				{/* WINNER IMAGE - end */}

				{/* DISCARDS - ini */}
				{(display_losers) && (
					<Group
						x={losers_inline_margin}
						y={size.current.w + losers_inline_margin}
					>
						{losers.map((player, p) => {
							const col = p % losers_per_row;
							const row = Math.ceil((p + 1) / losers_per_row) - 1;

							const loser_image = indexed_images[`discard_${player.id}`];
							if(loser_image == undefined) return;

							const loser_image_scale = (item_sizes.current.loser_size + 2) / loser_image?.image_w ?? 1;

							return (
								<Rect
									key={`loser_${p}`}
									x={col * (item_sizes.current.loser_size + padding.current)}
									y={row * (item_sizes.current.loser_size + padding.current)}
									width={item_sizes.current.loser_size}
									height={item_sizes.current.loser_size}
									fillPatternImage={loser_image.fillPatternImage}
									fillPatternRepeat='no-repeat'
									fillPatternScaleX={loser_image_scale}
									fillPatternScaleY={loser_image_scale}
									fillPatternOffsetX={(item_sizes.current.loser_size + 2) / 2 / loser_image_scale}
									fillPatternOffsetY={(item_sizes.current.loser_size + 2) / 2 / loser_image_scale}
									fillPatternX={(item_sizes.current.loser_size + 2) / 2}
									fillPatternY={(item_sizes.current.loser_size + 2) / 2}
								/>
							)
						})}
					</Group>
				)}
				{/* DISCARDS - end */}
			</Layer>
		</Stage>
	)
})

export default FaceOffFinalCanvas;